import React from 'react';

import {CommentsSidebarItem} from 'app/comments/components/CommentsSidebarItem';
import {GenericCommentsSidebar} from 'app/comments/components/GenericCommentsSidebar';
import {DetailPageActivitySidebar} from 'app/components/general/DetailPageActivitySidebar';
import {PageSidebarStructure} from 'app/general/structures/PageSidebarStructure';
import {SidebarItem} from 'app/general/structures/SidebarItem';
import {gt} from 'app/utils/i18n';
import {getPathFromURL} from 'app/utils/urls';

import {TaskDetailPageSidebarRelatedTasks} from './TaskDetailPageSidebarRelatedTasks';

import type {InjectedRouter} from 'react-router';
import type {
  ProcedureSerializedType,
  ProcessSerializedType,
  TaskConcreteInstanceSerializedType,
  TaskInstanceSerializedType,
} from 'sweet-types';

interface Props {
  task: TaskConcreteInstanceSerializedType | TaskInstanceSerializedType;
  object: ProcessSerializedType | ProcedureSerializedType;
  objectContentType: 'procedure' | 'proceduregroup';
  router: InjectedRouter;
  route: {
    props: {
      showComments?: boolean;
      showActivity?: boolean;
      showTasks?: boolean;
    };
  };
}

export function TaskDetailPageSidebar({
  task,
  object,
  objectContentType,
  route,
  router,
}: Props): JSX.Element | null {
  const {showComments, showActivity, showTasks} = route.props;

  const closeInnerSidebar = () => {
    router.replace(getPathFromURL(task.html_url));
  };

  if (task.id == null) {
    return null;
  }

  return (
    <PageSidebarStructure
      innerContent={
        showComments ? (
          <GenericCommentsSidebar
            contentType="taskinstance"
            addContentType="taskinstance"
            canDeleteComment={object.can_edit}
            mentionsContentType={objectContentType}
            mentionsObjectId={object.id}
            objectId={task.id}
            toggleSidebar={closeInnerSidebar}
          />
        ) : showActivity ? (
          <DetailPageActivitySidebar
            title={gt('Task Activity')}
            objectId={task.id}
            showExportButton={task.template.can_assign}
            contentType="taskinstance"
            contentArgs={{target: gt('this task')}}
            toggleSidebar={closeInnerSidebar}
          />
        ) : showTasks ? (
          <TaskDetailPageSidebarRelatedTasks
            toggleSidebar={closeInnerSidebar}
            task={task}
          />
        ) : null
      }
    >
      {(showTooltip, hideTooltip) => {
        const helpTooltipProps = {
          forceTooltipShow: showTooltip,
          onClick: hideTooltip,
        };

        if (task.id == null) {
          return null;
        }

        return (
          <>
            <SidebarItem
              title={gt('Task steps')}
              icon="far fa-file"
              onlyActiveOnIndex={true}
              link={getPathFromURL(task.html_url)}
              {...helpTooltipProps}
            />
            <CommentsSidebarItem
              contentType={'taskinstance'}
              objectId={task.id}
              link={getPathFromURL(task.comments_url)}
              {...helpTooltipProps}
            />
            <SidebarItem
              title={gt('Tasks in this series')}
              icon="fa fa-tasks"
              onlyActiveOnIndex={true}
              link={getPathFromURL(task.tasks_url)}
              {...helpTooltipProps}
            />
            <SidebarItem
              title={gt('Task Activity')}
              icon="fa fa-signal"
              onlyActiveOnIndex={true}
              link={getPathFromURL(task.activity_url)}
              {...helpTooltipProps}
            />
          </>
        );
      }}
    </PageSidebarStructure>
  );
}
